import { AdminShell } from "@project/admin/components/admin-shell";
import { ApiResourceTable } from "@project/admin/components/api-resource-table";
import { GaushalaSchemeFormFields } from "@project/admin/components/gaushala-scheme-form-fields";
import type { Field } from "@project/admin/components/resource-table";
import { gaushalaSchemeService } from "@project/services/admin/gaushala-scheme.service";
import {
  toGaushalaFeatures,
  type GaushalaScheme,
  type GaushalaSchemePayload,
} from "@project/types/gaushala";

const tableFields: Field[] = [
  {
    name: "title",
    label: "Title",
    showInTable: true,
    hidden: true,
  },
  {
    name: "short_title",
    label: "Short Title",
    showInTable: true,
    hidden: true,
  },
  {
    name: "duration_type",
    label: "Duration Type",
    showInTable: true,
    hidden: true,
  },
  {
    name: "duration",
    label: "Duration",
    type: "number",
    showInTable: true,
    hidden: true,
  },
  {
    name: "amount",
    label: "Amount (₹)",
    type: "number",
    showInTable: true,
    currency: true,
    hidden: true,
  },
  {
    name: "per_day_amount",
    label: "Per Day (₹)",
    type: "number",
    showInTable: true,
    currency: true,
    hidden: true,
  },
  {
    name: "features",
    label: "Features",
    type: "tags",
    showInTable: true,
    hidden: true,
  },
  {
    name: "button_text",
    label: "Button Text",
    showInTable: true,
    hidden: true,
  },
  {
    name: "display_order",
    label: "Display Order",
    type: "number",
    showInTable: true,
    hidden: true,
  },
  {
    name: "is_highlighted",
    label: "Highlighted",
    type: "boolean",
    showInTable: true,
    hidden: true,
  },
  {
    name: "is_active",
    label: "Active",
    type: "boolean",
    showInTable: true,
    hidden: true,
  },
];

function toPayload(payload: Record<string, unknown>): GaushalaSchemePayload {
  const title = String(payload.title ?? "").trim();
  if (!title) {
    throw new Error("Title is required.");
  }

  const numAmount = Number(payload.amount);
  if (!Number.isFinite(numAmount) || numAmount < 0) {
    throw new Error("Enter a valid amount.");
  }

  const features = toGaushalaFeatures(payload.features);

  return {
    title,
    short_title: String(payload.short_title ?? "").trim() || undefined,
    duration_type: String(payload.duration_type ?? "year").trim() || "year",
    duration:
      payload.duration !== undefined && payload.duration !== ""
        ? Number(payload.duration)
        : 1,
    amount: numAmount.toFixed(2),
    per_day_amount:
      payload.per_day_amount !== undefined && payload.per_day_amount !== ""
        ? Number(payload.per_day_amount).toFixed(2)
        : undefined,
    description: String(payload.description ?? "").trim() || undefined,
    button_text:
      String(payload.button_text ?? "SPONSOR NOW").trim() || "SPONSOR NOW",
    features,
    display_order:
      payload.display_order !== undefined && payload.display_order !== ""
        ? Number(payload.display_order)
        : 1,
    is_highlighted:
      payload.is_highlighted === true || payload.is_highlighted === "true",
    is_active: payload.is_active !== false && payload.is_active !== "false",
  };
}

export default function GaushalaSchemesPage() {
  return (
    <AdminShell
      title="Gaushala Schemes"
      subtitle="Manage Gau Seva sponsorship schemes shown on the website"
    >
      <ApiResourceTable<GaushalaScheme>
        title="Gaushala Scheme"
        labelField="title"
        fields={tableFields}
        searchFields={["title", "short_title", "description"]}
        defaultValues={{
          is_active: true,
          is_highlighted: false,
          display_order: 1,
          duration_type: "year",
          duration: 1,
          button_text: "SPONSOR NOW",
          features: [],
        }}
        statusFilterOptions={[
          { value: "active", label: "Active" },
          { value: "inactive", label: "Inactive" },
        ]}
        csvFileName="gaushala-schemes"
        dialogClassName="max-w-2xl"
        mapRecordToForm={record => ({
          ...record,
          features: Array.isArray(record.features) ? record.features : [],
        })}
        buildPayload={editing => toPayload(editing as Record<string, unknown>)}
        renderForm={({ editing, setEditing }) => (
          <GaushalaSchemeFormFields
            editing={editing as Record<string, unknown> | null}
            setEditing={value =>
              setEditing(value as Partial<GaushalaScheme> | null)
            }
          />
        )}
        list={query => gaushalaSchemeService.listPage(query)}
        create={payload =>
          gaushalaSchemeService.create(payload as GaushalaSchemePayload)
        }
        update={(id, payload) =>
          gaushalaSchemeService.update(
            id,
            payload as Partial<GaushalaSchemePayload>
          )
        }
        remove={id => gaushalaSchemeService.delete(id)}
      />
    </AdminShell>
  );
}
